import { createClient } from '@supabase/supabase-js'

// No frontend, NUNCA usar Service Key. Usar sempre Anon Key.
// Notas: Como a Anon Key está sujeita a RLS, algumas tabelas ou rotinas de debug podem falhar.
// Esse comportamento é o esperado em um ambiente seguro.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://supavps2.responda-ai.com'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

const supabaseClient = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  }
)

export async function inspectDatabase() {
  console.log('🔍 Inspecionando estrutura do banco de dados...')
  
  const results = {
    tables: {} as any,
    relationships: [] as any[],
    success: false,
    error: null as any
  }

  try {
    console.log('📋 Buscando tabelas...')
    const { data: tables, error: tablesError } = await supabaseClient
      .from('information_schema.tables')
      .select('table_name')
      .eq('table_schema', 'public')
      .eq('table_type', 'BASE TABLE')

    if (tablesError) {
      throw new Error(`Erro ao buscar tabelas: ${tablesError.message} (Isso pode ser limitação de permissão, o que é esperado no frontend)`)
    }

    for (const table of tables || []) {
      const tableName = table.table_name
      console.log(`📊 Analisando tabela: ${tableName}`)
      
      const { data: columns, error: columnsError } = await supabaseClient
        .from('information_schema.columns')
        .select('column_name, data_type, is_nullable, column_default')
        .eq('table_schema', 'public')
        .eq('table_name', tableName)
        .order('ordinal_position')

      if (columnsError) {
        console.warn(`⚠️ Erro ao buscar colunas de ${tableName}:`, columnsError.message)
        continue
      }

      const { data: sampleData, error: sampleError } = await supabaseClient
        .from(tableName)
        .select('*')
        .limit(1)

      const { count, error: countError } = await supabaseClient
        .from(tableName)
        .select('*', { count: 'exact', head: true })

      results.tables[tableName] = {
        columns: columns || [],
        sampleData: sampleData?.[0] || null,
        count: count || 0,
        errors: {
          columns: (columnsError as any)?.message,
          sample: sampleError?.message,
          count: countError?.message
        }
      }
    }

    console.log('🔗 Buscando relacionamentos...')
    const { data: relationships, error: relError } = await supabaseClient
      .from('information_schema.key_column_usage')
      .select(`
        table_name,
        column_name,
        referenced_table_name,
        referenced_column_name
      `)
      .eq('table_schema', 'public')
      .not('referenced_table_name', 'is', null)

    if (!relError) {
      results.relationships = relationships || []
    }

    results.success = true
    console.log('✅ Inspeção concluída com sucesso!')
    
    return results

  } catch (error) {
    console.error('❌ Erro na inspeção:', error)
    results.error = error instanceof Error ? error.message : 'Erro desconhecido'
    return results
  }
}

export async function testExpectedTables() {
  console.log('🎯 Testando tabelas esperadas: clientes, pedidos, livros')

  const expectedTables = ['clientes', 'pedidos', 'livros']
  const results = {} as any

  for (const tableName of expectedTables) {
    console.log(`🔍 Testando tabela: ${tableName}`)

    try {
      console.log(`Tentativa 1: Consulta padrão para ${tableName}`)
      const { data, error, count } = await supabaseClient
        .from(tableName)
        .select('*', { count: 'exact' })
        .limit(1)

      if (error) {
        console.log(`❌ Erro de leitura (Provável RLS atuando, o que é seguro): ${error.message}`)

        const { data: tableExists, error: schemaError } = await supabaseClient
          .from('information_schema.tables')
          .select('table_name')
          .eq('table_schema', 'public')
          .eq('table_name', tableName)
          .single()

        results[tableName] = {
          exists: !schemaError && !!tableExists,
          error: error.message,
          columns: [],
          sample: null,
          count: 0,
          methods_tried: ['standard_query', 'information_schema'],
          final_error: schemaError?.message || 'Tabela não encontrada ou acesso negado'
        }
        
      } else {
        const columns = data && data.length > 0 ? Object.keys(data[0]) : []

        results[tableName] = {
          exists: true,
          error: null,
          columns,
          sample: data?.[0] || null,
          count: count || 0,
          methods_tried: ['standard_query_success']
        }
      }
    } catch (err) {
      console.log(`❌ Erro geral: ${err}`)
      results[tableName] = {
        exists: false,
        error: err instanceof Error ? err.message : 'Erro desconhecido',
        columns: [],
        sample: null,
        count: 0,
        methods_tried: ['all_failed']
      }
    }
  }

  return results
}

export async function testTablesViaREST() {
  console.log('🌐 Testando tabelas via REST API direta (usando Anon Key)...')

  const possibleUrls = [
    'https://supavps2.responda-ai.com/rest/v1',
    'https://supavps2.responda-ai.com/project/default/rest/v1',
    'https://supavps2.responda-ai.com/api/rest/v1'
  ]

  const anonKey = supabaseAnonKey
  const expectedTables = ['clientes', 'pedidos', 'livros']
  const results = {} as any

  let workingUrl = null
  for (const testUrl of possibleUrls) {
    console.log(`🔍 Testando URL: ${testUrl}`)
    try {
      const response = await fetch(`${testUrl}/clientes?limit=1`, {
        method: 'GET',
        headers: {
          'apikey': anonKey,
          'Authorization': `Bearer ${anonKey}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok || response.status === 401 || response.status === 403) { 
        workingUrl = testUrl
        console.log(`✅ URL funcionando: ${testUrl}`)
        break
      }
    } catch (err) {
      console.log(`❌ URL não funciona: ${testUrl}`)
    }
  }

  if (!workingUrl) {
    return {
      error: 'Nenhuma URL da API REST funcionou',
      urls_tested: possibleUrls
    }
  }

  for (const tableName of expectedTables) {
    console.log(`🔍 Testando ${tableName} via REST...`)

    try {
      const response = await fetch(`${workingUrl}/${tableName}?limit=1`, {
        method: 'GET',
        headers: {
          'apikey': anonKey,
          'Authorization': `Bearer ${anonKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'count=exact'
        }
      })

      console.log(`Response status for ${tableName}:`, response.status)

      if (response.ok) {
        const data = await response.json()
        const count = response.headers.get('content-range')?.split('/')[1] || '0'

        results[tableName] = {
          exists: true,
          error: null,
          columns: data && data.length > 0 ? Object.keys(data[0]) : [],
          sample: data?.[0] || null,
          count: parseInt(count),
          method: 'rest_api_success',
          url_used: workingUrl
        }
      } else {
        const errorData = await response.text()
        console.log(`Error for ${tableName}:`, errorData)

        results[tableName] = {
          exists: response.status !== 404, 
          error: `HTTP ${response.status}: ${errorData}`,
          columns: [],
          sample: null,
          count: 0,
          method: 'rest_api_failed',
          url_used: workingUrl
        }
      }
    } catch (err) {
      results[tableName] = {
        exists: false,
        error: err instanceof Error ? err.message : 'Erro desconhecido',
        columns: [],
        sample: null,
        count: 0,
        method: 'rest_api_error',
        url_used: workingUrl
      }
    }
  }

  return results
}

export async function testConnectivity() {
  console.log('🔗 Testando conectividade básica...')

  const tests = [
    {
      name: 'Supabase Main',
      url: 'https://supavps2.responda-ai.com',
      description: 'URL principal do Supabase'
    },
    {
      name: 'REST API v1',
      url: 'https://supavps2.responda-ai.com/rest/v1',
      description: 'Endpoint da API REST'
    },
    {
      name: 'Auth API',
      url: 'https://supavps2.responda-ai.com/auth/v1',
      description: 'Endpoint de autenticação'
    }
  ]

  const results = []

  for (const test of tests) {
    console.log(`🔍 Testando: ${test.name}`)

    try {
      const response = await fetch(test.url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const reachable = response.ok || response.status === 401 || response.status === 403

      results.push({
        name: test.name,
        url: test.url,
        status: response.status,
        ok: reachable,
        description: response.status === 401 || response.status === 403
          ? `${test.description} (auth exigida — esperado)`
          : test.description,
        error: null
      })

      console.log(`✅ ${test.name}: ${response.status}`)

    } catch (err) {
      results.push({
        name: test.name,
        url: test.url,
        status: 'ERROR',
        ok: false,
        description: test.description,
        error: err instanceof Error ? err.message : 'Erro desconhecido'
      })

      console.log(`❌ ${test.name}: ${err}`)
    }
  }

  return results
}

export async function createTestData() {
  console.log('⚠️ ATENÇÃO: Esta função criará dados de teste no banco!')
  console.log('🛑 NÃO EXECUTE sem permissão explícita!')
  
  return {
    success: false,
    message: 'Função bloqueada por segurança. Execute apenas com permissão explícita.'
  }
}

export function generateTypesFromStructure(tableStructure: any) {
  let typesCode = `// Tipos gerados automaticamente da estrutura do banco\n\n`
  
  typesCode += `export interface Database {\n`
  typesCode += `  public: {\n`
  typesCode += `    Tables: {\n`
  
  for (const [tableName, info] of Object.entries(tableStructure) as [string, any][]) {
    if (!info.exists) continue
    
    typesCode += `      ${tableName}: {\n`
    typesCode += `        Row: {\n`
    
    if (info.sample) {
      for (const [key, value] of Object.entries(info.sample)) {
        const type = typeof value === 'string' ? 'string' : 
                    typeof value === 'number' ? 'number' : 
                    typeof value === 'boolean' ? 'boolean' : 
                    value === null ? 'string | null' : 'any'
        
        typesCode += `          ${key}: ${type}\n`
      }
    }
    
    typesCode += `        }\n`
    typesCode += `        Insert: {\n`
    
    if (info.sample) {
      for (const [key, value] of Object.entries(info.sample)) {
        const type = typeof value === 'string' ? 'string' : 
                    typeof value === 'number' ? 'number' : 
                    typeof value === 'boolean' ? 'boolean' : 
                    value === null ? 'string | null' : 'any'
        
        const optional = key === 'id' || key.includes('created_at') || key.includes('updated_at') ? '?' : ''
        typesCode += `          ${key}${optional}: ${type}\n`
      }
    }
    
    typesCode += `        }\n`
    typesCode += `        Update: {\n`
    
    if (info.sample) {
      for (const [key, value] of Object.entries(info.sample)) {
        const type = typeof value === 'string' ? 'string' : 
                    typeof value === 'number' ? 'number' : 
                    typeof value === 'boolean' ? 'boolean' : 
                    value === null ? 'string | null' : 'any'
        
        typesCode += `          ${key}?: ${type}\n`
      }
    }
    
    typesCode += `        }\n`
    typesCode += `      }\n`
  }
  
  typesCode += `    }\n`
  typesCode += `  }\n`
  typesCode += `}\n`
  
  return typesCode
}